import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import { trackStoryView } from '../utils/analytics';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import Spinner from 'react-bootstrap/Spinner';
import Image from 'react-bootstrap/Image';
import Modal from 'react-bootstrap/Modal';
import StoryService from '../services/story';
import Cropper from 'react-easy-crop';
import { useTheme } from '../context/ThemeContext';
import './StoryDetail.css';
const StoryDetail = () => {
  const { storyId } = useParams();
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Add item form state
  const [showAddModal, setShowAddModal] = useState(false);
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaType, setMediaType] = useState('PHOTO');
  const [mediaPreview, setMediaPreview] = useState(null);
  const [addItemLoading, setAddItemLoading] = useState(false);
  const [addItemError, setAddItemError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  
  // Link state
  const [addLink, setAddLink] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkLabel, setLinkLabel] = useState('');
  const [linkPosition, setLinkPosition] = useState({ x: 0.5, y: 0.5 });
  const [isDraggingLink, setIsDraggingLink] = useState(false);
  
  // Image cropping state
  const [showCropModal, setShowCropModal] = useState(false);
  const [croppedImage, setCroppedImage] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  useEffect(() => {
    fetchStory();
  }, [storyId]);
  
  // Clean up event listeners when component unmounts
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleLinkDragMove);
      document.removeEventListener('mouseup', handleLinkDragEnd);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStory = async () => {
    try {
      setLoading(true);
      const data = await StoryService.getStoryById(storyId);
      setStory(data);
      setError('');
      
      // Track the story view using the utility function
      trackStoryView(storyId);
    } catch (err) {
      setError('Failed to load story: ' + (err.response?.data || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleMediaChange = (e) => {
    let file;
    
    // Handle both drag-drop and file input
    if (e.dataTransfer) {
      file = e.dataTransfer.files[0];
    } else if (e.target.files) {
      file = e.target.files[0];
    }
    
    if (file) {
      // Check if file is an image or video based on mediaType
      if (mediaType === 'PHOTO' && !file.type.match('image.*')) {
        setAddItemError('Please select an image file');
        return;
      }
      
      if (mediaType === 'VIDEO' && !file.type.match('video.*')) {
        setAddItemError('Please select a video file');
        return;
      }
      
      // Check file size (max 60MB)
      if (file.size > 60 * 1024 * 1024) {
        setAddItemError('File size should be less than 60MB');
        return;
      }
      
      setMediaFile(file);
      
      // Create preview
      if (mediaType === 'PHOTO') {
        const reader = new FileReader();
        reader.onload = () => {
          setMediaPreview(reader.result);
          // Don't automatically show crop modal - make it optional
        };
        reader.readAsDataURL(file);
      } else {
        // For videos, just set the preview URL
        setMediaPreview(URL.createObjectURL(file));
      }
      
      setAddItemError('');
    }
  };
  
  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };
  
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };
// Handle link position change when clicking on the container
const handleLinkPositionChange = (e) => {
  if (!addLink || !mediaPreview) return;
  
  // If we're already dragging, don't update position on container click
  if (isDraggingLink) return;
  
  const container = e.currentTarget;
  const rect = container.getBoundingClientRect();
  
  // Calculate relative position (0-1)
  const x = (e.clientX - rect.left) / rect.width;
  const y = (e.clientY - rect.top) / rect.height;
  
  // Clamp values between 0 and 1
  const clampedX = Math.max(0, Math.min(1, x));
  const clampedY = Math.max(0, Math.min(1, y));
  
  setLinkPosition({ x: clampedX, y: clampedY });
};

// Handle link drag start
const handleLinkDragStart = (e) => {
  e.stopPropagation(); // Prevent container click event
  setIsDraggingLink(true);
  
  // Add mouse move and mouse up event listeners to document
  document.addEventListener('mousemove', handleLinkDragMove);
  document.addEventListener('mouseup', handleLinkDragEnd);
};

// Handle link drag move
const handleLinkDragMove = (e) => {
  if (!isDraggingLink || !addLink || !mediaPreview) return;
  
  // Find the container
  const container = document.querySelector('.link-position-container');
  if (!container) return;
  
  const rect = container.getBoundingClientRect();
  
  // Calculate relative position (0-1)
  const x = (e.clientX - rect.left) / rect.width;
  const y = (e.clientY - rect.top) / rect.height;
  
  // Clamp values between 0 and 1
  const clampedX = Math.max(0, Math.min(1, x));
  const clampedY = Math.max(0, Math.min(1, y));
  
  setLinkPosition({ x: clampedX, y: clampedY });
};

// Handle link drag end
const handleLinkDragEnd = () => {
  setIsDraggingLink(false);
  
  // Remove event listeners
  document.removeEventListener('mousemove', handleLinkDragMove);
  document.removeEventListener('mouseup', handleLinkDragEnd);
};

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    handleMediaChange(e);
  };

  const handleAddItem = async () => {
    // Validate form
    if (!mediaFile) {
      setAddItemError('Please select a media file');
      return;
    }
    
    // Validate link URL if adding a link
    if (addLink && !linkUrl) {
      setAddItemError('Please enter a valid URL for the link');
      return;
    }
    // Validate link label if adding a link
    if (addLink && !linkLabel.trim()) {
      setAddItemError('Please enter a label for the link - this will be displayed on the story');
      return;
    }
    
    
    try {
      setAddItemLoading(true);
      
      // Prepare the file to upload
      let fileToUpload;
      
      if (croppedImage) {
        // If we have a cropped image, use its blob
        fileToUpload = croppedImage.blob;
      } else {
        // Otherwise use the original file
        fileToUpload = mediaFile;
      }
      
      // Calculate the next order number
      const nextOrder = story.items.length > 0
        ? Math.max(...story.items.map(item => item.order)) + 1
        : 1;
      
      // Prepare link data if adding a link
      const linkData = addLink ? {
        linkUrl: linkUrl,
        linkLabel: linkLabel,
        linkPositionX: linkPosition.x,
        linkPositionY: linkPosition.y
      } : {};
      
      const response = await StoryService.addStoryItem(
        storyId,
        fileToUpload,
        mediaType,
        nextOrder,
        addLink ? linkUrl : null,
        addLink ? linkLabel : null,
        addLink ? linkPosition.x : null,
        addLink ? linkPosition.y : null
      );
      
      if (response && response.storyItemId) {
        // Close modal and refresh story data
        setShowAddModal(false);
        setShowCropModal(false);
        setMediaFile(null);
        setMediaPreview(null);
        setCroppedImage(null);
        // Reset link state
        setAddLink(false);
        setLinkUrl('');
        setLinkLabel('');
        setLinkPosition({ x: 0.5, y: 0.5 });
        fetchStory();
      } else {
        setAddItemError('Failed to add content. Please try again.');
      }
    } catch (err) {
      setAddItemError('Failed to add content: ' + (err.response?.data || err.message));
    } finally {
      setAddItemLoading(false);
    }
  };

  // Handle crop complete
  const onCropComplete = (croppedArea, croppedAreaPixels) => {
    console.log('Crop complete:', croppedAreaPixels);
    setCroppedAreaPixels(croppedAreaPixels);
  };

  // Create a cropped image
  const getCroppedImg = () => {
    try {
      console.log('Getting cropped image with pixels:', croppedAreaPixels);
      
      if (!croppedAreaPixels) {
        setAddItemError('Please select a crop area first');
        return;
      }
      
      // Create a canvas element
      const canvas = document.createElement('canvas');
      canvas.width = croppedAreaPixels.width;
      canvas.height = croppedAreaPixels.height;
      
      const ctx = canvas.getContext('2d');
      
      // Create an image object
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.src = mediaPreview;
      
      // When the image is loaded, draw it on the canvas
      image.onload = () => {
        try {
          // Draw the cropped image
          ctx.drawImage(
            image,
            croppedAreaPixels.x,
            croppedAreaPixels.y,
            croppedAreaPixels.width,
            croppedAreaPixels.height,
            0,
            0,
            croppedAreaPixels.width,
            croppedAreaPixels.height
          );
          
          // Convert canvas to blob
          canvas.toBlob((blob) => {
            if (!blob) {
              console.error('Failed to create blob');
              setAddItemError('Failed to crop image: could not create blob');
              return;
            }
            
            console.log('Created blob with size:', blob.size);
            
            // Use the blob directly instead of creating a File object
            const blobUrl = URL.createObjectURL(blob);
            
            // Create a simple object to store the blob and its URL
            const croppedFileObj = {
              blob: blob,
              url: blobUrl,
              name: mediaFile.name,
              type: 'image/jpeg',
              lastModified: Date.now()
            };
            
            setCroppedImage(croppedFileObj);
            setShowCropModal(false);
          }, 'image/jpeg', 1);
        } catch (drawError) {
          console.error('Error drawing image:', drawError);
          setAddItemError('Failed to crop image: ' + drawError.message);
        }
      };
      
      // Handle image loading errors
      image.onerror = (error) => {
        console.error('Error loading image for crop:', error);
        setAddItemError('Failed to load image for cropping');
      };
    } catch (error) {
      console.error('Error in getCroppedImg:', error);
      setAddItemError('Failed to crop image: ' + error.message);
    }
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric',   hour: 'numeric', minute: 'numeric', second: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">{error}</Alert>
        <Button variant="primary" onClick={() => navigate('/stories')}>
          Back to Stories
        </Button>
      </Container>
    );
  }

  if (!story) {
    return (
      <Container className="mt-5">
        <Alert variant="warning">Story not found</Alert>
        <Button variant="primary" onClick={() => navigate('/stories')}>
          Back to Stories
        </Button>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <Card className="mb-4">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex flex-column align-items-center">
              <Image
                src={story.circularImageUrl}
                roundedCircle
                style={{ width: '100px', height: '100px', objectFit: 'cover', marginTop: '5px' }}
                className="mb-2"
              />
              <h4 className="mb-0 text-center">{story.text || ''}</h4>
            </div>
            <div>
              <Button
                variant="primary"
                onClick={() => navigate(`/story/${storyId}/studio`)}
                className="me-2"
              >
                Add Content
              </Button>
              <Button variant="secondary" onClick={() => navigate('/stories')}>
                Back
              </Button>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          <p>
            <strong>Created by:</strong> {story.username}<br />
            <strong>Created at:</strong> {formatDate(story.createdAt)}<br />
            <strong>Total Content:</strong> {story.items.length}
          </p>
        </Card.Body>
      </Card>

      {story.items.length === 0 ? (
        <Alert variant="info">
          This story doesn't have any items yet. Click "Add Content" to add photos or videos.
        </Alert>
      ) : (
        <Row xs={1} md={2} className="g-4">
          {story.items.sort((a, b) => a.order - b.order).map((item) => (
            <Col key={item.id}>
              <Card>
                <Card.Header>Item #{item.order}</Card.Header>
                <Card.Body>
                  {item.mediaType === 'PHOTO' ? (
                    <Image
                      src={item.mediaUrl}
                      fluid
                      style={{ maxHeight: '300px', objectFit: 'contain' }}
                      className="mb-3 w-100"
                    />
                  ) : (
                    <video
                      controls
                      className="w-100 mb-3"
                      style={{ maxHeight: '300px' }}
                    >
                      <source src={item.mediaUrl} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  )}
                  <p className="mb-0">
                    <small>Added: {formatDate(item.createdAt)}</small>
                  </p>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Add Item Modal */}
      <Modal
        show={showAddModal}
        onHide={() => {
          setShowAddModal(false);
          setMediaFile(null);
          setMediaPreview(null);
          setCroppedImage(null);
          setAddLink(false);
          setLinkUrl('');
          setLinkLabel('');
          setLinkPosition({ x: 0.5, y: 0.5 });
          setAddItemError('');
        }}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Content</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {addItemError && <Alert variant="danger">{addItemError}</Alert>}
          
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Media Type</Form.Label>
              <Form.Select
                value={mediaType}
                onChange={(e) => setMediaType(e.target.value)}
              >
                <option value="PHOTO">Photo</option>
                <option value="VIDEO">Video</option>
              </Form.Select>
              <Form.Text className="text-muted">
                {mediaType === 'PHOTO'
                  ? 'Photos must be in 1080x1920 resolution (9:16 aspect ratio).'
                  : 'Videos should be less than 20 seconds long.'}
              </Form.Text>
            </Form.Group>

            {/* Link option - only for photos */}
            {mediaType === 'PHOTO' && mediaPreview && (
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  id="add-link-checkbox"
                  label="Add a link to this story item"
                  checked={addLink}
                  onChange={(e) => setAddLink(e.target.checked)}
                  className="mb-2"
                />
                
                {addLink && (
                  <>
                    <Form.Label>Link URL</Form.Label>
                    <Form.Control
                      type="url"
                      placeholder="https://example.com"
                      value={linkUrl}
                      onChange={(e) => setLinkUrl(e.target.value)}
                      className="mb-3"
                    />
                    <Form.Label>Link Label <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="MYWEBSITE.COM"
                      value={linkLabel}
                      onChange={(e) => setLinkLabel(e.target.value.toUpperCase())}
                      className="mb-3"
                      maxLength={30}
                      required
                    />
                    <Form.Text className="text-muted mb-1 d-block">
                      The label will be displayed on the story and is required (max 30 characters)
                    </Form.Text>
                    
                    <Form.Text className="text-muted mb-2 d-block">
                      Position the link by clicking on the image below or drag the marker:
                    </Form.Text>
                    
                    <div
                      className="link-position-container"
                      onClick={handleLinkPositionChange}
                    >
                      <Image
                        src={mediaPreview}
                        fluid
                      />
                      
                      {/* Link position indicator */}
                      <div
                        className="link-position-marker"
                        style={{
                          left: `${linkPosition.x * 100}%`,
                          top: `${linkPosition.y * 100}%`
                        }}
                        onMouseDown={handleLinkDragStart}
                        onMouseUp={handleLinkDragEnd}
                      >
                        <i className="bi bi-link-45deg"></i>
                      </div>
                    </div>
                  </>
                )}
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Upload {mediaType.toLowerCase()}</Form.Label>
              <div
                className={`file-drop-area ${isDragging ? 'dragging' : ''} ${mediaFile ? 'file-selected' : ''}`}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <div className="file-drop-icon">
                  <i className="bi bi-plus-circle"></i>
                </div>
                <div className="file-drop-message">
                  Drag & drop your {mediaType.toLowerCase()} here
                </div>
                <div className="file-input-container">
                  <Form.Control
                    type="file"
                    accept={mediaType === 'PHOTO' ? 'image/*' : 'video/*'}
                    onChange={handleMediaChange}
                    className="file-input"
                    id="fileInput"
                  />
                  <Button
                    variant={mediaFile ? "success" : "primary"}
                    size="sm"
                    className="browse-button"
                  >
                    {mediaFile ? "File Selected ✓" : "Select File"}
                  </Button>
                  {mediaFile && (
                    <div className="text-success mt-2 small">
                      <i className="bi bi-check-circle-fill me-1"></i>
                      File "{mediaFile.name}" selected
                    </div>
                  )}
                </div>
                <div className="file-format-hint">
                  {mediaType === 'PHOTO'
                    ? 'Supported formats: JPG, PNG, GIF (max 10MB)'
                    : 'Supported formats: MP4, MOV, WebM (max 10MB)'}
                </div>
              </div>
            </Form.Group>

            {mediaPreview && mediaType === 'PHOTO' && !showCropModal && (
              <div className="text-center mb-3">
                <p>Preview:</p>
                <Image
                  src={mediaPreview}
                  fluid
                  style={{ maxHeight: '300px', objectFit: 'contain' }}
                />
                {croppedImage && <p className="text-success mt-2">Image cropped successfully!</p>}
                <div className="mt-3">
                  {/*
                  <Button
                    variant="outline-primary"
                    onClick={() => setShowCropModal(true)}
                    className="me-2"
                  >
                    Crop Image (Optional)
                  </Button>
                  */}
                </div>
              </div>
            )}

            {mediaPreview && mediaType === 'VIDEO' && (
              <div className="text-center mb-3">
                <p>Preview:</p>
                <video
                  controls
                  className="w-100"
                  style={{ maxHeight: '300px' }}
                >
                  <source src={mediaPreview} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAddItem}
            disabled={addItemLoading || !mediaFile}
          >
            {addItemLoading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Adding...
              </>
            ) : (
              'Add Item'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Image Crop Modal */}
      <Modal show={showCropModal} onHide={() => setShowCropModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Crop Image (Optional)</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">
            Crop your image to 9:16 aspect ratio (1080x1920) for optimal display.
            This step is optional - you can skip it if you prefer to use the original image.
          </p>
          <div className="text-center">
            <p className="text-muted mb-3">Drag to position and use the slider to zoom (9:16 aspect ratio):</p>
            
            {/* Cropper container */}
            <div style={{ position: 'relative', height: '400px', width: '100%' }}>
              <Cropper
                image={mediaPreview}
                crop={crop}
                zoom={zoom}
                aspect={9 / 16}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
              />
            </div>
            
            {/* Zoom control */}
            <div className="mt-3 mb-3" style={{ width: '100%', maxWidth: '500px', margin: '0 auto' }}>
              <label htmlFor="zoom-slider" className="form-label">Zoom: {zoom.toFixed(1)}x</label>
              <input
                type="range"
                className="form-range"
                id="zoom-slider"
                min="1"
                max="3"
                step="0.1"
                value={zoom}
                onChange={(e) => setZoom(parseFloat(e.target.value))}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCropModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={getCroppedImg}
            disabled={false} // Always enable the button
          >
            Crop Image
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default StoryDetail;